import type { MatchConfig, PlayerProfile, TeamProfile, TeamSide, WorldSnapshot } from '../core/contracts';

export const HALF_S = 45 * 60;

const pad = (n: number): string => (n < 10 ? '0' : '') + n;

export function mmss(totalS: number): string {
  const s = Math.max(0, Math.floor(totalS));
  return `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
}

/** Scorebug clock: main clock caps at 45:00/90:00; stoppage runs in a separate "+m:ss" pill. */
export function clockParts(snap: Pick<WorldSnapshot, 'matchClock' | 'half' | 'phase'>): { main: string; extra: string | null; period: string } {
  const cap = snap.half === 1 ? HALF_S : HALF_S * 2;
  const c = snap.matchClock;
  const period = snap.phase === 'half_time' ? 'HT' : snap.phase === 'full_time' ? 'FT' : snap.half === 1 ? '1ST' : '2ND';
  if (snap.phase === 'half_time') return { main: '45:00', extra: null, period };
  if (snap.phase === 'full_time') return { main: '90:00', extra: null, period };
  if (c > cap) return { main: mmss(cap), extra: '+' + mmss(c - cap).replace(/^0/, ''), period };
  return { main: mmss(c), extra: null, period };
}

/** Broadcast minute notation: 23', 45+2', 90+4'. */
export function minuteLabel(matchClock: number): string {
  const m = Math.floor(matchClock / 60) + 1;
  if (matchClock >= HALF_S * 2) return `90+${m - 90}'`;
  return `${m}'`;
}

/** Like minuteLabel but knows the half (first-half stoppage shows 45+N'). */
export function minuteInHalf(matchClock: number, half: 1 | 2): string {
  if (half === 1 && matchClock >= HALF_S) return `45+${Math.floor((matchClock - HALF_S) / 60) + 1}'`;
  return minuteLabel(matchClock);
}

export function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

/** Readable ink (near-black or white) for text on a team colour. */
export function inkFor(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '#fff';
  const n = parseInt(m[1]!, 16);
  const lin = (v: number): number => { const c = v / 255; return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
  const L = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  return L > 0.42 ? '#0b0e14' : '#ffffff';
}

export interface PlayerRef { profile: PlayerProfile; team: TeamProfile }

export function indexPlayers(config: MatchConfig): Map<string, PlayerRef> {
  const m = new Map<string, PlayerRef>();
  for (const team of [config.home, config.away]) {
    for (const p of team.players) m.set(p.id, { profile: p, team });
    for (const p of team.bench) m.set(p.id, { profile: p, team });
  }
  return m;
}

export const team = (config: MatchConfig, side: TeamSide): TeamProfile => (side === 'home' ? config.home : config.away);

export const POSITION_LABEL: Record<PlayerProfile['position'], string> = {
  GK: 'Goalkeeper', DEF: 'Defender', MID: 'Midfielder', FWD: 'Forward',
};

export const FORMATION_LINES: Record<TeamProfile['formation'], number[]> = {
  '4-4-2': [4, 4, 2], '4-3-3': [4, 3, 3], '4-2-3-1': [4, 2, 3, 1], '3-5-2': [3, 5, 2],
};
